import { createInstance } from 'localforage';
import pick from 'lodash.pick';
import { create, StoreApi } from 'zustand';
import { UseBoundStore } from 'zustand';
import {
  createJSONStorage,
  devtools as devtoolsMiddleware,
  persist as persistMidleware,
} from 'zustand/middleware';

import { Commander, CommanderActions } from '@spazious/dollhouse-commander';
import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { DollhouseStore } from '@spazious/dollhouse-engine';
import { FSetupGraph } from '@spazious/dollhouse-fsetup';
import { History } from '@spazious/dollhouse-history';
import { rts } from '@spazious/dollhouse-rts-peer';
import { FShallowGraph, GraphAction } from '@spazious/dollhouse-shallow-graph';
import { StagingArea } from '@spazious/dollhouse-staging-area';
import { World } from '@spazious/dollhouse-three';

import { StorageMiddleware } from './middlewares/index.js';
import { stateSlice } from './stateSlice.js';
import { RootState, Store } from './types.js';

type StateKeys = Exclude<keyof RootState<GraphAction, CommanderActionTypesEnum>, 'get' | 'set'>;

export interface StoreInitializerFunction<
  TAction extends GraphAction,
  TMeta extends CommanderActionTypesEnum
> {
  (store: UseBoundStore<StoreApi<RootState<TAction, TMeta>>>):
    | Promise<Partial<RootState<TAction, TMeta>>>
    | Partial<RootState<TAction, TMeta>>
    | void;
}

export type CreateStoreOptions<
  TAction extends GraphAction,
  TMeta extends CommanderActionTypesEnum
> = {
  name?: string;
  devtools?: boolean;
  persistKeys?: StateKeys[];
  initializer?: StoreInitializerFunction<TAction, TMeta>;
};

export const storePersistPrefix = 'spz-dollhouse';
export const storePersistVersion = 1.0;
export const storePersistStrategy = 'localStorage';
export const storePersistKeys = [
  'history',
  'theme',
  'session',
  'camera',
  'controls',
  'settings',
] satisfies StateKeys[];

export const createStore = <
  TAction extends GraphAction = GraphAction,
  TMeta extends CommanderActionTypesEnum = CommanderActionTypesEnum
>(
  dollhouse: DollhouseStore,
  graph: FSetupGraph,
  staging: StagingArea<TAction, TMeta>,
  history: History<TAction[], TMeta>,
  world: World,
  commander: Commander<FShallowGraph, CommanderActions, TMeta>,
  opts?: CreateStoreOptions<TAction, TMeta>
): Store<TAction, TMeta> => {
  const replacer = (key: string, value: any) => {
    if (key === 'history') {
      return history.toJSON();
    }

    if (key === 'staging') {
      return staging.toJSON();
    } else if (key === 'staging') {
      return staging.toJSON();
    } else if (key === 'graph') {
      return graph.toJSON();
    }

    return ['gl', 'world', 'scene', 'get', 'set'].includes(key) ? undefined : value;
  };

  const reviver = (key: string, value: any) => {
    if (key === 'history' && value) {
      try {
        return history.fromJSON(value);
      } catch (e) {
        console.warn(e);
      }
    } else if (key === 'staging' && value) {
      try {
        return staging.fromJSON(value);
      } catch (e) {
        console.warn(e);
      }
    } else if (key === 'graph' && value) {
      try {
        return graph.fromJSON(value);
      } catch (e) {
        console.warn(e);
      }
    }
    return value;
  };

  const storage = new StorageMiddleware(
    storePersistStrategy === 'localStorage'
      ? createJSONStorage(() => localStorage, {
          replacer,
          reviver,
        })
      : (createInstance({
          name: storePersistPrefix,
          version: storePersistVersion,
        }) as any)
  );

  const {
    devtools = false,
    name = 'app',
    initializer,
    persistKeys = storePersistKeys,
  } = { ...opts };
  const storePersistName = `${storePersistPrefix}-${name}`;
  const persistedStore = persistMidleware(
    rts(stateSlice(graph, staging, history, world, commander), {
      partialize: (state) => pick(state, ['history', 'staging']),
      replacer,
      reviver,
    }),
    {
      name: storePersistName,
      version: storePersistVersion,
      storage,
      skipHydration: true,
      partialize: (state) => pick(state, persistKeys),
    }
  );

  const store = devtools
    ? create(
        devtoolsMiddleware(persistedStore, {
          serialize: {
            replacer,
            // reviver,
            options: {
              undefined: undefined,
            },
          },
          name: `Spazious Dollhouse ${
            name.charAt(0).toUpperCase() + name.slice(1).toLocaleLowerCase()
          }`,
          enabled: devtools,
        })
      )
    : create(persistedStore);

  graph.subscribe(() => {
    store.setState({ tick: performance.now() });
  });

  if (initializer) {
    const setterFn = async () => {
      const retValue = initializer(store);

      if (retValue) {
        const newState = retValue instanceof Promise ? await retValue : retValue;
        store.setState(newState);
      }
    };

    if (store.persist) {
      store.persist.onFinishHydration(setterFn);
    } else {
      setterFn();
    }
  }

  const typedStore = store as unknown as Store<TAction, TMeta>;
  typedStore.dollhouse = dollhouse;
  typedStore.staging = staging;
  typedStore.history = history;
  typedStore.appName = name;

  if (store.persist && !store.persist.hasHydrated()) {
    store.persist.rehydrate();
  }

  return Object.freeze(typedStore);
};
